/**
 * Gallery Embed - Standard iframe-based calculator embedding for gallery previews
 * Uses the same pattern as wiki.html for consistency across the codebase
 * Now pulls real calculator data from WikiDB cache instead of hardcoded data
 *
 * PERFORMANCE: Uses persistent React root to avoid remounting on every click.
 * Pre-fetches full calc data in background for instant loading.
 */

const galleryEmbedBuildTime = document.lastModified;
console.log('[Gallery Embed] Module loaded - Last modified:', galleryEmbedBuildTime);

window.wikiDB = null;

// Cache for pre-fetched full calc data (slug -> calc with rows)
window._galleryCalcCache = new Map();

// Persistent React root manager - uses Action.renderCalcInto() for proper context wrapping
window.GalleryEmbedManager = {
    container: null,
    footerEl: null,
    currentCalcId: null,
    _renderResult: null,

    /**
     * Mount/prepare container (call once when gallery panel opens)
     */
    mount(container, footerEl = null) {
        if (!window.Action || !window.Action.renderCalcInto) {
            console.warn('[GalleryEmbedManager] Action.renderCalcInto not available');
            return false;
        }

        // If already mounted to same container, skip
        if (this.container === container) {
            return true;
        }

        // Unmount existing if any
        this.unmount();

        this.container = container;
        this.footerEl = footerEl;

        // Clear container
        container.innerHTML = '';

        console.log('[GalleryEmbedManager] Mounted persistent root');
        return true;
    },

    /**
     * Show a calc using Action.renderCalcInto() (instant - proper context)
     */
    async showCalc(calc, options = {}) {
        const startTime = performance.now();

        if (!this.container) {
            console.warn('[GalleryEmbedManager] Not mounted, falling back to renderGalleryCalc');
            return window.renderGalleryCalc(calc, options.container, options);
        }

        // Check cache first for pre-fetched data
        const cacheKey = calc.slug || calc.id;
        let fullCalc = window._galleryCalcCache.get(cacheKey);

        if (fullCalc) {
            console.log('[GalleryEmbedManager] Cache hit for', cacheKey);
            calc = fullCalc;
        } else {
            // Check if we have valid rows
            const hasValidRows = Array.isArray(calc.rows) && calc.rows.length > 0 &&
                                calc.rows.some(r => r && r.text);

            if (!hasValidRows) {
                // Need to fetch - this is the slow path
                console.log('[GalleryEmbedManager] Cache miss, fetching', cacheKey);
                fullCalc = await window._fetchFullCalcFromWiki(calc);
                if (fullCalc && fullCalc.rows?.length > 0) {
                    calc = fullCalc;
                    window._galleryCalcCache.set(cacheKey, calc);
                }
            }
        }

        // Apply display mode (collapse rows)
        if (options.displayMode && Array.isArray(calc.rows)) {
            calc = { ...calc, rows: calc.rows.map(row => ({ ...row, hidden: true })) };
        }

        // Ensure wiki type
        if (!calc.type) calc.type = 'wiki';
        if (calc.id && !calc.id.startsWith('wiki-')) {
            calc.remoteId = calc.remoteId || calc.slug || calc.id;
            calc.id = `wiki-${calc.slug || calc.id}`;
        }

        // Use the centralized Action.renderCalcInto() utility
        // This handles ActionProvider wrapping, React root management, etc.
        const footerEl = this.footerEl || options.footerEl;

        this._renderResult = window.Action.renderCalcInto(this.container, calc, {
            action: window.Embed,
            calcViewMode: 'embed',
            footerEl: footerEl
        });

        this.currentCalcId = calc.id;

        const elapsed = performance.now() - startTime;
        console.log(`[GalleryEmbedManager] showCalc completed in ${elapsed.toFixed(1)}ms`);
    },

    /**
     * Unmount and cleanup
     */
    unmount() {
        if (this._renderResult && this._renderResult.unmount) {
            this._renderResult.unmount();
            this._renderResult = null;
        }
        if (this.container) {
            this.container.innerHTML = '';
        }
        this.container = null;
        this.footerEl = null;
        this.currentCalcId = null;
        console.log('[GalleryEmbedManager] Unmounted');
    }
};

/**
 * Pre-fetch full calc data for a list of calcs (call during gallery init)
 * Fetches in background without blocking UI
 */
window.prefetchGalleryCalcs = async function(calcs, limit = 20) {
    if (!calcs || calcs.length === 0) return;

    // Only prefetch first N calcs (most likely to be clicked)
    const toPrefetch = calcs.slice(0, limit).filter(c => {
        const key = c.slug || c.id;
        // Skip if already cached or has valid rows
        if (window._galleryCalcCache.has(key)) return false;
        const hasValidRows = Array.isArray(c.rows) && c.rows.length > 0 &&
                            c.rows.some(r => r && r.text);
        return !hasValidRows;
    });

    if (toPrefetch.length === 0) {
        console.log('[Gallery Prefetch] All calcs already cached or have rows');
        return;
    }

    console.log(`[Gallery Prefetch] Pre-fetching ${toPrefetch.length} calcs in background...`);

    // Fetch in parallel with concurrency limit
    const CONCURRENCY = 3;
    for (let i = 0; i < toPrefetch.length; i += CONCURRENCY) {
        const batch = toPrefetch.slice(i, i + CONCURRENCY);
        await Promise.all(batch.map(async (calc) => {
            try {
                const fullCalc = await window._fetchFullCalcFromWiki(calc);
                if (fullCalc && fullCalc.rows?.length > 0) {
                    const key = calc.slug || calc.id;
                    window._galleryCalcCache.set(key, fullCalc);
                }
            } catch (e) {
                // Ignore prefetch errors
            }
        }));
    }

    console.log(`[Gallery Prefetch] Cached ${window._galleryCalcCache.size} calcs`);
};

/**
 * Initialize gallery embed with WikiDB
 */
window.initGalleryEmbed = async function() {
    if (window.wikiDB) return;

    try {
        window.wikiDB = new WikiDB();
        await window.wikiDB.init();
        console.log('[Gallery Embed] WikiDB initialized');
    } catch (e) {
        console.error('[Gallery Embed] Failed to initialize WikiDB:', e);
    }
};

/**
 * Render a calculator from wiki data
 * @param {string|object} calcNameOrObj - Either a slug/id string or a full calc object
 * @param {HTMLElement} targetEl - Container element to render into
 */
window.renderGalleryCalc = async function(calcNameOrObj, targetEl, options = {}) {
    // Ensure WikiDB is initialized
    if (!window.wikiDB) {
        await window.initGalleryEmbed();
    }

    try {
        let calc;

        // If passed a full calc object, use it directly
        if (typeof calcNameOrObj === 'object' && calcNameOrObj !== null) {
            calc = calcNameOrObj;
            console.log('[Gallery Embed] renderGalleryCalc() using provided calc object:', calc.slug || calc.id);
        } else {
            // Otherwise search by slug/id/name
            const calcName = calcNameOrObj;
            console.log('[Gallery Embed] renderGalleryCalc() searching for:', calcName);

            // Try to get calc from WikiDB by ID/slug/name
            calc = await window.wikiDB.getCalcById(calcName);

            // If not found by exact ID, search in all calculators
            if (!calc) {
                console.log('[Gallery Embed] Not found by ID, searching all calculators...');
                const allCalcs = await window.wikiDB.getCalculators();
                calc = allCalcs.find(c =>
                    c.title === calcName ||
                    c.name === calcName ||
                    c.id === calcName ||
                    c.slug === calcName ||
                    c.id === calcName.toLowerCase().replace(/\s+/g, '-')
                );
            }

            if (!calc) {
                console.error('[Gallery Embed] Calculator not found:', calcName);
                throw new Error('Calculator not found: ' + calcName);
            }
        }

        console.log('[Gallery Embed] Found calc:', calc.id, '- Title:', calc.title);
        console.log('[Gallery Embed] Calc rows type:', typeof calc.rows, '- Is array:', Array.isArray(calc.rows));
        if (Array.isArray(calc.rows)) {
            console.log('[Gallery Embed] Calc rows length:', calc.rows.length, '- First row:', calc.rows[0]);
        } else {
            console.log('[Gallery Embed] Calc rows:', calc.rows);
        }

        // If calc has no rows/content OR rows are empty objects, fetch full data from wiki API
        const hasValidRows = Array.isArray(calc.rows) && calc.rows.length > 0 &&
                            calc.rows.some(r => r && Object.keys(r).length > 0);

        if (!hasValidRows) {
            console.log('[Gallery Embed] Fetching full calc data from wiki API (no valid rows)...');
            const fullCalc = await window._fetchFullCalcFromWiki(calc);
            console.log('[Gallery Embed] API response:', fullCalc ? `received ${fullCalc.rows?.length || 0} rows` : 'null');
            if (fullCalc && fullCalc.rows && fullCalc.rows.length > 0) {
                calc = fullCalc;
                console.log('[Gallery Embed] ✓ Using fetched calc with', calc.rows.length, 'rows');
            } else {
                console.warn('[Gallery Embed] Could not fetch full calc data, using cached calc');
            }
        } else {
            console.log('[Gallery Embed] Using cached calc with', calc.rows.length, 'valid rows');
        }

        // Normalize calc structure if needed
        if (!calc.rows && calc.content) {
            // Try to parse content into rows (basic parsing)
            console.log('[Gallery Embed] Parsing content into rows...');
            calc.rows = window._parseCalcContent(calc.content);
        }

        console.log('[Gallery Embed] Ready to render with', calc.rows ? calc.rows.length : 0, 'rows');

        // If displayMode is true, collapse all rows (set hidden: true)
        if (options.displayMode && Array.isArray(calc.rows)) {
            console.log('[Gallery Embed] Display mode enabled - collapsing all rows');
            calc.rows = calc.rows.map(row => ({
                ...row,
                hidden: true
            }));
        }

        // Ensure calc is marked as wiki type before saving (prevents pollution of user's My Calcs)
        if (!calc.type) {
            calc.type = 'wiki';
        }
        // Ensure ID has wiki- prefix for consistency
        if (calc.id && !calc.id.startsWith('wiki-')) {
            calc.remoteId = calc.remoteId || calc.slug || calc.id;
            calc.id = `wiki-${calc.slug || calc.id}`;
        }

        // Save to Embed API (standard pattern - same as wiki)
        if (!window.Embed) {
            console.warn('[Gallery Embed] window.Embed not available');
            return;
        }
        window.Embed.save(calc);

    // Clear container and create render target
    targetEl.innerHTML = '';
    const container = document.createElement('div');
    container.id = 'gallery-calc-root';
    container.className = 'gallery-embed-container';
    targetEl.appendChild(container);

    // Use the centralized Action.renderCalcInto() utility
    try {
        if (!window.Action || !window.Action.renderCalcInto) {
            console.warn('[Gallery Embed] Action.renderCalcInto not available');
            return;
        }

        console.log('[Gallery Embed] Rendering calc into container...');

        window.Action.renderCalcInto(container, calc, {
            action: window.Embed,
            calcViewMode: 'embed',
            footerEl: options.footerEl
        });

        console.log('[Gallery Embed] ✓ Rendered into div');
    } catch (e) {
        console.error('[Gallery Embed] Failed to render calc:', e.message);
    }
    } catch (e) {
        console.error('[Gallery Embed] Error rendering calculator:', e);
        targetEl.innerHTML = '<p class="text-theme-dim">Failed to load calculator</p>';
    }
};

/**
 * Fetch full calculator data from wiki API (including rows/content)
 */
window._fetchFullCalcFromWiki = async function(calcRef) {
    try {
        const fallbackName = typeof calcRef === 'object'
            ? (calcRef?.name || calcRef?.title || '')
            : '';
        const generatedSlug = fallbackName
            ? fallbackName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
            : '';
        const slug = typeof calcRef === 'string'
            ? calcRef
            : (calcRef?.slug || generatedSlug || calcRef?.id || '').toString();
        const id = typeof calcRef === 'object'
            ? (calcRef?.id || null)
            : slug;

        if (!slug) {
            console.warn('[Gallery Embed] Cannot fetch calc without slug/id');
            return null;
        }

        console.log('[Gallery Embed] Fetching full calc from API. slug:', slug, 'id:', id);

        // Use instacalc.com/api/wiki/create endpoint (always use live server, not local emulator)
        const url = 'https://instacalc.com/api/wiki/create';
        const body = {
            slug,
            name: typeof calcRef === 'object' ? (calcRef?.name || calcRef?.title || slug) : slug,
            rows: []
        };

        if (id && id !== slug) {
            body.id = id;
        }

        console.log('[Gallery Embed] Fetch URL:', url);
        console.log('[Gallery Embed] Request body:', body);

        const startTime = performance.now();
        const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });
        const fetchTime = performance.now() - startTime;

        console.log('[Gallery Embed] API response received in', fetchTime.toFixed(2), 'ms - Status:', response.status);

        if (!response.ok) {
            console.warn('[Gallery Embed] API error:', response.status, response.statusText);
            return null;
        }

        const apiResponse = await response.json();
        console.log('[Gallery Embed] Received data from API');
        console.log('[Gallery Embed] Response keys:', Object.keys(apiResponse).slice(0, 10));

        // API returns { created: bool, calc: {...} } - extract the calculator
        const data = apiResponse.calc || apiResponse;

        console.log('[Gallery Embed] Extracted calc data');
        console.log('[Gallery Embed] Has rows:', !!data.rows, '- Row count:', data.rows?.length || 0);
        if (data.rows && data.rows.length > 0) {
            console.log('[Gallery Embed] First row keys:', Object.keys(data.rows[0]).slice(0, 10));
            console.log('[Gallery Embed] First row:', JSON.stringify(data.rows[0]).substring(0, 200));
        }

        // Normalize the response
        // Use slug as the canonical ID for wiki calcs (ensures consistency)
        const calcId = slug || data.slug || data.id || id;
        const calc = {
            id: `wiki-${calcId}`,  // Prefix with 'wiki-' to distinguish from user calcs
            slug: data.slug || slug,
            title: data.name || data.title || 'Untitled',
            name: data.name || data.title || 'Untitled',
            description: data.description || '',
            rows: data.rows || [],
            content: data.content || '',
            category: data.category || (data.tags && data.tags.length > 0 ? data.tags[0] : 'general'),
            tags: data.tags || [],
            type: 'wiki',  // CRITICAL: Mark as wiki calc so it goes to session store, not localStorage
            remoteId: calcId,  // Original slug for Firebase reference
            published: { id: calcId },  // Required for getPublicURL
            stats: {
                views: data.views || data.stats?.views || 0,
                likes: data.likes || data.stats?.likes || 0
            },
            updatedAt: data.updatedAt || new Date(data.updated).getTime(),
            createdAt: data.createdAt || Date.now()
        };

        console.log('[Gallery Embed] Processed calc:', calc.title, 'with', calc.rows.length, 'rows');
        return calc;
    } catch (e) {
        console.error('[Gallery Embed] Error fetching full calc:', e.message);
        console.error('[Gallery Embed] Error stack:', e.stack);
        console.error('[Gallery Embed] Error type:', e.constructor.name);
        return null;
    }
};

/**
 * Helper function to parse calculator content into rows
 * Basic implementation - can be extended based on actual content format
 */
window._parseCalcContent = function(content) {
    // If content is already structured, return it
    if (typeof content === 'object' && Array.isArray(content)) {
        return content;
    }

    // If content is a string, try to parse it
    if (typeof content === 'string') {
        try {
            const parsed = JSON.parse(content);
            if (Array.isArray(parsed)) return parsed;
        } catch (e) {
            // Not JSON, return empty
        }
    }

    // Fallback: return empty array
    return [];
};
